import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface DemoMetrics {
  totalProducts: number;
  activeProducts: number;
  pendingApprovals: number;
  lowStockItems: number;
  monthlyRevenue: number;
  // Add other metrics as needed
  [key: string]: any;
}

interface DemoMetricsState {
  metrics: DemoMetrics | null;
  isDemoMode: boolean;
  isLoading: boolean;
  error: string | null;
}

const DEFAULT_METRICS: DemoMetrics = {
  totalProducts: 150,
  activeProducts: 142,
  pendingApprovals: 8,
  lowStockItems: 12,
  monthlyRevenue: 45000,
};

const initialState: DemoMetricsState = {
  metrics: null,
  isDemoMode: false,
  isLoading: false,
  error: null,
};

const demoMetricsSlice = createSlice({
  name: 'demoMetrics',
  initialState,
  reducers: {
    // Initialize demo metrics from localStorage on app start
    initializeDemoMetrics: (state) => {
      try {
        const storedMetrics = localStorage.getItem('demoMetrics');
        if (storedMetrics) {
          const parsedMetrics = JSON.parse(storedMetrics);
          state.metrics = parsedMetrics;
          state.isDemoMode = true;
        } else {
          // Initialize with default metrics if not present
          state.metrics = DEFAULT_METRICS;
          state.isDemoMode = true;
          // Save to localStorage
          localStorage.setItem('demoMetrics', JSON.stringify(DEFAULT_METRICS));
        }
      } catch (error) {
        console.warn('Failed to load demo metrics from localStorage:', error);
        state.metrics = DEFAULT_METRICS;
        state.isDemoMode = true;
        localStorage.setItem('demoMetrics', JSON.stringify(DEFAULT_METRICS));
      }
    },
    // Update demo metrics
    updateDemoMetrics: (state, action: PayloadAction<Partial<DemoMetrics>>) => {
      if (state.metrics) {
        state.metrics = { ...state.metrics, ...action.payload };
        // Save to localStorage
        try {
          localStorage.setItem('demoMetrics', JSON.stringify(state.metrics));
        } catch (error) {
          console.warn('Failed to save demo metrics to localStorage:', error);
        }
      }
    },
    // Set demo mode
    setDemoMode: (state, action: PayloadAction<boolean>) => {
      state.isDemoMode = action.payload;
      if (action.payload && !state.metrics) {
        state.metrics = DEFAULT_METRICS;
        localStorage.setItem('demoMetrics', JSON.stringify(DEFAULT_METRICS));
      } else if (!action.payload) {
        state.metrics = null;
        localStorage.removeItem('demoMetrics');
      }
    },
    // Reset to default metrics
    resetToDefaultMetrics: (state) => {
      state.metrics = DEFAULT_METRICS;
      state.isDemoMode = true;
      localStorage.setItem('demoMetrics', JSON.stringify(DEFAULT_METRICS));
    },
    // Clear demo metrics
    clearDemoMetrics: (state) => {
      state.metrics = null;
      state.isDemoMode = false;
      state.error = null;
      state.isLoading = false;
      localStorage.removeItem('demoMetrics');
    },
    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    // Set error state
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const { 
  initializeDemoMetrics, 
  updateDemoMetrics, 
  setDemoMode, 
  resetToDefaultMetrics, 
  clearDemoMetrics, 
  setLoading, 
  setError 
} = demoMetricsSlice.actions;

export default demoMetricsSlice.reducer;


